import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle, Eye, Loader2, Mail, MessageCircle, RefreshCw, ShieldCheck, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { buildLeadWhatsAppUrl } from "@/lib/lead-whatsapp";
import { useAppDialogs } from "@/components/ui/app-dialogs";
import { AdminGlassPanel } from "./AdminGlassPanel";
import { OnboardingSessionsPanel } from "./OnboardingSessionsPanel";
import type { OnboardingSession } from "@/lib/self-service-provisioning";
import type { OnboardingPaymentAttempt } from "@/lib/onboarding-payment-attempts";
import { clientRequestHeaders, ensureCsrfToken } from "@/lib/sm-api";

type PaywayDiag = {
  configured: boolean;
  environment: string;
  health: { ok: boolean; detail?: string };
  uala?: { configured: boolean; environment: string | null; health: { ok: boolean; detail?: string } };
  selfService?: { enabled: boolean } | null;
  paymentAttempts?: OnboardingPaymentAttempt[];
  onboarding?: {
    byProvider: Array<{ provider: string; status: string; count: number }>;
    attemptsByProvider: Array<{ provider: string; status: string; count: number }>;
  };
  platform?: {
    volumeByProvider: Array<{ provider: string; status: string; count: number; cents: number }>;
    tenantConfigs: Array<{ provider: string; environment: string; tenants: number }>;
    webhooks: Record<string, number>;
  };
  recentEvents: Array<{
    id: string;
    eventType: string;
    paymentId: string | null;
    amountCents: number | null;
    status: string | null;
    createdAt: string;
    provider?: string | null;
  }>;
};

function fmtMoney(cents: number | null | undefined, currency = "ARS") {
  if (cents == null) return "—";
  return new Intl.NumberFormat("es-AR", { style: "currency", currency }).format(cents / 100);
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

/** Diagnóstico accionable: intento/evento + contacto + CTAs (no un log pelado). */
function CobrosDiagnosticsTab({
  onOpenSessions,
}: {
  onOpenSessions: () => void;
}) {
  const [data, setData] = useState<PaywayDiag | null>(null);
  const [sessions, setSessions] = useState<OnboardingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [payRes, sessRes] = await Promise.all([
        fetch("/api/admin/payway", { credentials: "include" }),
        fetch("/api/admin/onboarding", { credentials: "include" }),
      ]);
      const pay = (await payRes.json()) as PaywayDiag;
      const sess = (await sessRes.json()) as { sessions?: OnboardingSession[] };
      setData(pay);
      setSessions(sess.sessions ?? []);
    } catch {
      toast.error("No se pudo cargar el diagnóstico de cobros");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const byId = useMemo(() => {
    const m = new Map<string, OnboardingSession>();
    for (const s of sessions) m.set(s.id, s);
    return m;
  }, [sessions]);

  const act = async (sessionId: string, action: string) => {
    setActing(sessionId + action);
    try {
      await ensureCsrfToken();
      const res = await fetch("/api/admin/onboarding", {
        method: "POST",
        headers: { ...clientRequestHeaders(), "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sessionId, action }),
      });
      const body = (await res.json()) as { error?: string };
      if (!res.ok) throw new Error(body.error ?? "Error");
      toast.success("Acción aplicada");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setActing(null);
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando diagnóstico…
      </div>
    );
  }

  const attempts = data.paymentAttempts ?? [];
  const plat = data.platform;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">Diagnóstico de billing</p>
          <p className="text-xs text-muted-foreground">
            Contexto útil: contacto, plan, método/provider, estado y acción — no solo un log.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" /> Actualizar
          </Button>
          <Button variant="outline" size="sm" onClick={onOpenSessions}>
            Ver sesiones
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-border/50 bg-secondary/10 p-3">
          <p className="text-[11px] uppercase text-muted-foreground">Self-service</p>
          <p className="text-sm font-semibold">{data.selfService?.enabled === false ? "OFF" : "ON"}</p>
        </div>
        <div className="rounded-lg border border-border/50 bg-secondary/10 p-3">
          <p className="text-[11px] uppercase text-muted-foreground">Payway / Ualá</p>
          <p className="text-sm font-semibold">
            {data.configured ? "Payway OK" : "Payway —"} · {data.uala?.configured ? "Ualá OK" : "Ualá —"}
          </p>
        </div>
        <div className="rounded-lg border border-border/50 bg-secondary/10 p-3">
          <p className="text-[11px] uppercase text-muted-foreground">Webhooks 30d</p>
          <p className="text-sm font-semibold">
            ok {plat?.webhooks.webhook ?? 0} · dup {plat?.webhooks.duplicate ?? 0} · err {plat?.webhooks.error ?? 0}
          </p>
        </div>
      </div>

      {plat && plat.volumeByProvider.length > 0 ? (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Volumen por provider (30 días)</p>
          <div className="grid gap-1 sm:grid-cols-2">
            {plat.volumeByProvider.map((v) => (
              <div key={`${v.provider}-${v.status}`} className="flex justify-between rounded-md border px-2 py-1 text-xs">
                <span>{v.provider} · {v.status || "—"}</span>
                <span>{v.count} · {fmtMoney(v.cents)}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div>
        <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
          Intentos de pago con contexto ({attempts.length})
        </p>
        {attempts.length === 0 ? (
          <p className="rounded-lg border border-dashed py-8 text-center text-sm text-muted-foreground">
            Sin intentos. Cuando alguien pague en /contratar van a aparecer acá con contacto y CTAs.
          </p>
        ) : (
          <div className="space-y-2">
            {attempts.map((a) => {
              const s = a.sessionId ? byId.get(a.sessionId) : undefined;
              const failed = a.status === "failed";
              return (
                <div key={a.id} className="rounded-lg border bg-card p-3 space-y-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium truncate">
                          {s?.businessName ?? (a.sessionId ? `Sesión ${a.sessionId.slice(0, 8)}…` : "Sin sesión")}
                        </p>
                        <Badge variant={a.status === "approved" ? "secondary" : failed ? "destructive" : "outline"}>
                          {a.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {s?.ownerName ? `${s.ownerName} · ` : ""}
                        {s?.ownerEmail ?? "sin email"}
                        {s?.ownerPhone ? ` · ${s.ownerPhone}` : ""}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Plan <strong>{s?.planId ?? "—"}</strong>
                        {" · "}
                        {s?.billingMode ?? "—"}
                        {" · "}
                        Provider <strong>{a.provider}</strong>
                        {" · "}
                        {fmtMoney(a.amountCents, a.currency ?? "ARS")}
                      </p>
                      {a.errorMessage ? (
                        <p className="text-xs text-destructive">⚠ {a.errorMessage}</p>
                      ) : null}
                    </div>
                    <p className="text-[11px] text-muted-foreground shrink-0">{fmtDate(a.createdAt)}</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {s ? (
                      <>
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={onOpenSessions}>
                          <Eye className="h-3 w-3" /> Sesión
                        </Button>
                        {s.ownerPhone ? (
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" asChild>
                            <a
                              href={buildLeadWhatsAppUrl({
                                nombre: s.ownerName ?? "",
                                negocio: s.businessName ?? "",
                                email: s.ownerEmail ?? "",
                                telefono: s.ownerPhone,
                                planInteresado: s.planId,
                              })}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <MessageCircle className="h-3 w-3" /> WhatsApp
                            </a>
                          </Button>
                        ) : null}
                        {s.ownerEmail ? (
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" asChild>
                            <a
                              href={`mailto:${s.ownerEmail}?subject=${encodeURIComponent(`Alta Structura — ${s.businessName ?? ""}`)}&body=${encodeURIComponent(`Hola ${s.ownerName ?? ""},\n\nVimos un intento de pago (${a.status}) para ${s.businessName ?? "tu tienda"}. ¿Te ayudo a completarlo?\n`)}`}
                            >
                              <Mail className="h-3 w-3" /> Email
                            </a>
                          </Button>
                        ) : null}
                        {s.status === "payment_pending" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs gap-1"
                            disabled={acting === s.id + "confirm_payment"}
                            onClick={() => void act(s.id, "confirm_payment")}
                          >
                            {acting === s.id + "confirm_payment" ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                            Confirmar pago
                          </Button>
                        ) : null}
                        {(s.status === "payment_confirmed" || s.status === "failed") && !s.tenantId ? (
                          <Button
                            size="sm"
                            className="h-7 text-xs gap-1"
                            disabled={acting === s.id + "provision"}
                            onClick={() => void act(s.id, "provision")}
                          >
                            {acting === s.id + "provision" ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                            Provisionar
                          </Button>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">Sin sesión vinculada — solo evento técnico.</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function CobrosOpsPanel() {
  const { confirm } = useAppDialogs();
  const [tab, setTab] = useState<"diagnostico" | "sesiones">("diagnostico");
  const [purging, setPurging] = useState(false);
  const [sessionsKey, setSessionsKey] = useState(0);

  const purgeAll = async () => {
    const ok = await confirm({
      title: "¿Borrar TODAS las sesiones de onboarding?",
      description:
        "Se eliminan sesiones de /contratar e intentos de pago asociados. No toca tenants ya provisionados ni suscripciones activas. Esta acción no se puede deshacer.",
      confirmLabel: "Borrar todo",
      destructive: true,
    });
    if (!ok) return;
    setPurging(true);
    try {
      await ensureCsrfToken();
      const res = await fetch("/api/admin/onboarding", {
        method: "POST",
        headers: { ...clientRequestHeaders(), "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ action: "purge_all" }),
      });
      const data = (await res.json()) as { error?: string; deletedSessions?: number; deletedAttempts?: number };
      if (!res.ok) throw new Error(data.error ?? "Error al purgar");
      toast.success(`Purgado: ${data.deletedSessions ?? 0} sesiones · ${data.deletedAttempts ?? 0} intentos`);
      setSessionsKey((k) => k + 1);
      setTab("sesiones");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setPurging(false);
    }
  };

  return (
    <AdminGlassPanel
      title="Operaciones / Diagnóstico"
      description="Diagnóstico accionable de cobros y sesiones de onboarding — en pestañas separadas."
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)} className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            <TabsTrigger value="diagnostico">Diagnóstico</TabsTrigger>
            <TabsTrigger value="sesiones">Sesiones onboarding</TabsTrigger>
          </TabsList>
          {tab === "sesiones" ? (
            <Button
              variant="destructive"
              size="sm"
              className="gap-1.5"
              disabled={purging}
              onClick={() => void purgeAll()}
            >
              {purging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Vaciar sesiones
            </Button>
          ) : null}
        </div>

        <TabsContent value="diagnostico" className="mt-0">
          <CobrosDiagnosticsTab onOpenSessions={() => setTab("sesiones")} />
        </TabsContent>
        <TabsContent value="sesiones" className="mt-0">
          <OnboardingSessionsPanel key={sessionsKey} embedded />
        </TabsContent>
      </Tabs>
    </AdminGlassPanel>
  );
}
